/**
 * Business outcome / value framing (BA-light) — intake + pack projection.
 * Not BPMN / value-stream studio. Soft framing for board credibility.
 *
 * @typedef {{ en: string, pl: string }} LocText
 * @typedef {{
 *   schemaVersion: string,
 *   updatedAt: string,
 *   outcomeId: string,
 *   successSignalId: (string|null),
 *   headline: LocText,
 *   detail: LocText,
 *   outcomeLabel: LocText,
 *   successLabel: (LocText|null),
 * }} BusinessOutcomeFraming
 */

const { L } = require('./localizedText');

const SCHEMA_VERSION = '1.0.0';

const OUTCOME_OPTIONS = Object.freeze([
    {
        id: 'grow_revenue',
        label: L('Grow revenue or conversion', 'Wzrost przychodu lub konwersji'),
        description: L(
            'More sales, ARPU, or funnel completion from this architecture.',
            'Więcej sprzedaży, ARPU lub domykania lejka dzięki tej architekturze.',
        ),
    },
    {
        id: 'cut_cost',
        label: L('Cut cost or waste', 'Obniżenie kosztów lub marnotrawstwa'),
        description: L(
            'Lower run/build cost, fewer hand-offs, or retire duplicate systems.',
            'Niższy koszt run/build, mniej hand-offów lub wyłączenie duplikatów.',
        ),
    },
    {
        id: 'reduce_risk',
        label: L('Reduce risk or compliance exposure', 'Zmniejszenie ryzyka lub ekspozycji compliance'),
        description: L(
            'Fewer outages, audit findings, or security/privacy incidents.',
            'Mniej awarii, ustaleń audytu lub incydentów security/privacy.',
        ),
    },
    {
        id: 'speed_delivery',
        label: L('Ship faster / shorter time-to-value', 'Szybsze dostarczanie / krótszy time-to-value'),
        description: L(
            'Earlier MVP, go-live, or experiment cycles with less rework.',
            'Wcześniejsze MVP, go-live lub cykle eksperymentów z mniejszym przerobem.',
        ),
    },
    {
        id: 'unlock_capability',
        label: L('Unlock a new capability', 'Odblokowanie nowej zdolności'),
        description: L(
            'Enable a product or ops capability the current estate cannot support.',
            'Umożliwienie zdolności produktu lub ops, której obecny park nie wspiera.',
        ),
    },
    {
        id: 'validate_idea',
        label: L('Validate an idea before scaling', 'Walidacja pomysłu przed skalą'),
        description: L(
            'Learn cheaply whether the problem and solution fit — not a full platform yet.',
            'Tanie sprawdzenie, czy problem i rozwiązanie pasują — jeszcze nie pełna platforma.',
        ),
    },
]);

const SUCCESS_SIGNAL_OPTIONS = Object.freeze([
    {
        id: 'time_to_value',
        label: L('Time to first value', 'Czas do pierwszej wartości'),
        description: L(
            'Weeks to a usable slice in production or a paying pilot.',
            'Tygodnie do użytecznego wycinka na produkcji lub płatnego pilota.',
        ),
    },
    {
        id: 'adoption',
        label: L('Adoption / usage', 'Adopcja / użycie'),
        description: L(
            'Active users, tenants, or API call volume that proves pull.',
            'Aktywni użytkownicy, tenanci lub wolumen API potwierdzający popyt.',
        ),
    },
    {
        id: 'reliability',
        label: L('Reliability / SLA', 'Niezawodność / SLA'),
        description: L(
            'Uptime, latency, or error budget targets stakeholders will hold you to.',
            'Cele uptime, latencji lub error budgetu, których będą wymagać interesariusze.',
        ),
    },
    {
        id: 'cost_envelope',
        label: L('Cost envelope', 'Koperta kosztowa'),
        description: L(
            'Stay inside a monthly run or delivery budget band.',
            'Utrzymanie się w miesięcznym paśmie kosztów run lub dostawy.',
        ),
    },
    {
        id: 'compliance_gate',
        label: L('Compliance / audit gate', 'Brama compliance / audytu'),
        description: L(
            'Pass a named control review before broader rollout.',
            'Przejście nazwanego przeglądu kontroli przed szerszym rolloutem.',
        ),
    },
]);

const OUTCOME_BY_ID = Object.fromEntries(OUTCOME_OPTIONS.map((o) => [o.id, o]));
const SUCCESS_BY_ID = Object.fromEntries(SUCCESS_SIGNAL_OPTIONS.map((o) => [o.id, o]));

const BA_OUTCOME_INTENTS = new Set([
    'pack',
    'hld',
    'estimate',
    'optimize',
    'migration',
    'systems_integration',
    'change_on_platform',
    'iot',
]);

/**
 * @param {string|null|undefined} intentId
 * @returns {boolean}
 */
function needsBusinessOutcomeQuestion(intentId) {
    return BA_OUTCOME_INTENTS.has(String(intentId || ''));
}

/**
 * @param {Record<string, string>} answers
 * @param {string|null|undefined} intentId
 * @returns {boolean}
 */
function needsSuccessSignalQuestion(answers = {}, intentId) {
    return needsBusinessOutcomeQuestion(intentId) && Boolean(answers.business_outcome);
}

function optionLabelsMap(options) {
    const map = {};
    for (const o of options) {
        map[o.id] = { en: o.label.en, pl: o.label.pl };
    }
    return map;
}

/**
 * Deterministic framing for requirements / briefing / PM lens.
 * @param {Record<string, string>} answers
 * @returns {BusinessOutcomeFraming|null}
 */
function buildBusinessOutcomeFraming(answers = {}) {
    const outcomeId = String(answers.business_outcome || '').trim();
    if (!outcomeId || !OUTCOME_BY_ID[outcomeId]) return null;

    const outcome = OUTCOME_BY_ID[outcomeId];
    const successId = String(answers.success_signal || '').trim();
    const success = SUCCESS_BY_ID[successId] || null;

    const headline = L(
        `Business outcome: ${outcome.label.en}`,
        `Wynik biznesowy: ${outcome.label.pl}`,
    );
    const detail = success
        ? L(
            `${outcome.description.en} Success signal: ${success.label.en.toLowerCase()} — ${success.description.en}`,
            `${outcome.description.pl} Sygnał sukcesu: ${success.label.pl.toLowerCase()} — ${success.description.pl}`,
        )
        : L(
            `${outcome.description.en} Confirm how you will measure success in the next step.`,
            `${outcome.description.pl} W następnym kroku potwierdź, jak zmierzysz sukces.`,
        );

    return {
        schemaVersion: SCHEMA_VERSION,
        updatedAt: new Date().toISOString(),
        outcomeId,
        successSignalId: success ? successId : null,
        headline,
        detail,
        outcomeLabel: outcome.label,
        successLabel: success ? success.label : null,
    };
}

/**
 * Append one sentence to the requirements product summary.
 * @param {string} summaryEn
 * @param {string} summaryPl
 * @param {BusinessOutcomeFraming|null|undefined} framing
 * @returns {{ en: string, pl: string }}
 */
function appendOutcomeToProductSummary(summaryEn, summaryPl, framing) {
    if (!framing?.outcomeLabel) {
        return { en: summaryEn, pl: summaryPl };
    }
    const successEn = framing.successLabel
        ? ` Measured by ${framing.successLabel.en.toLowerCase()}.`
        : '';
    const successPl = framing.successLabel
        ? ` Mierzone przez: ${framing.successLabel.pl.toLowerCase()}.`
        : '';
    return {
        en: `${summaryEn} Business aim: ${framing.outcomeLabel.en.toLowerCase()}.${successEn}`,
        pl: `${summaryPl} Cel biznesowy: ${framing.outcomeLabel.pl.toLowerCase()}.${successPl}`,
    };
}

/**
 * Guided question — primary business outcome (requirements step).
 * @returns {object}
 */
function buildBusinessOutcomeQuestion() {
    return {
        id: 'business_outcome',
        stepId: 'requirements',
        prompt: L(
            'What business outcome should this architecture primarily serve?',
            'Jakiemu wynikowi biznesowemu ma przede wszystkim służyć ta architektura?',
        ),
        help: L(
            'One primary aim keeps the board pack honest — not a full value-stream map.',
            'Jeden główny cel utrzymuje pack uczciwy — to nie pełna mapa strumienia wartości.',
        ),
        options: OUTCOME_OPTIONS.map((o) => ({
            id: o.id,
            label: o.label,
            description: o.description,
        })),
    };
}

/**
 * Guided question — how success will be judged (after outcome).
 * @returns {object}
 */
function buildSuccessSignalQuestion() {
    return {
        id: 'success_signal',
        stepId: 'requirements',
        prompt: L(
            'How will you know the outcome is working?',
            'Po czym poznacie, że wynik działa?',
        ),
        help: L(
            'Pick the signal sponsors will actually track — not every KPI.',
            'Wybierz sygnał, który sponsorzy naprawdę będą śledzić — nie każdy KPI.',
        ),
        options: SUCCESS_SIGNAL_OPTIONS.map((o) => ({
            id: o.id,
            label: o.label,
            description: o.description,
        })),
    };
}

module.exports = {
    SCHEMA_VERSION,
    OUTCOME_OPTIONS,
    SUCCESS_SIGNAL_OPTIONS,
    BA_OUTCOME_INTENTS,
    needsBusinessOutcomeQuestion,
    needsSuccessSignalQuestion,
    buildBusinessOutcomeFraming,
    appendOutcomeToProductSummary,
    buildBusinessOutcomeQuestion,
    buildSuccessSignalQuestion,
    answerLabelsForOutcome: () => optionLabelsMap(OUTCOME_OPTIONS),
    answerLabelsForSuccess: () => optionLabelsMap(SUCCESS_SIGNAL_OPTIONS),
};
